import { aliases, mdi } from 'vuetify/iconsets/mdi'

import type { ThemeDefinition, VuetifyOptions } from 'vuetify'

interface CreateVuetifyParams {
  /**
   * Extra components to be manually registered.
   */
  extraComponents?: Record<string, object>
  /**
   * Include component defaults used in the main app.
   */
  useAppDefaults: boolean
}

/**
 * Generates shared Vuetify options for use across multiple apps.
 */
export function createVuetifyOptions (params: CreateVuetifyParams): VuetifyOptions {
  const light: ThemeDefinition = {
    colors: {
      background: '#F9F9F9',
      darknavyblue: '#182C4F',
      navyblue: '#1A3056',
      panelblue: '#F3F7FC',
      faintblue: '#F0F7FF',
      alertblue: '#DDE8F6',
      lightblue: '#4A90E2',
      lightgrey: '#FAFAFA',
      subheadgrey: '#9B9B9B',
      placeholdergrey: '#C4C4C4',
      bordergrey: '#E5E5E5',
      alternaterow: '#F6F6F6',
      error: '#F17F74',
      warning: '#FCC35B',
      info: '#2196F3',
      highlight: '#9D4CAF',
      success: '#55D19A',
      primary: '#1976D2',
      diffremoved: '#FAE1E1',
      diffadded: '#E1FAE3',
      disabled: '#EFEFEF',
    },
  }
  const dark: ThemeDefinition = {
    dark: true,
    colors: {
      ...light.colors,
      background: '#212121',
      panelblue: '#0A2832',
      faintblue: '#243447',
      lightgrey: '#393939',
      bordergrey: '#7C7C7C',
      alternaterow: '#444444',
      disabled: '#101010',
    },
  }

  const pinkColors = {
    darknavyblue: '#990052',
    navyblue: '#A60059',
  }
  const pinkLight: ThemeDefinition = {
    colors: {
      ...light.colors,
      ...pinkColors,
    },
  }
  const pinkDark: ThemeDefinition = {
    dark: true,
    colors: {
      ...dark.colors,
      ...pinkColors,
    },
  }

  const opts: VuetifyOptions = {
    display: {
      thresholds: {
        md: 960,
        lg: 1280,
        xl: 1920,
        xxl: 2560,
      },
    },
    icons: {
      defaultSet: 'mdi',
      aliases,
      sets: {
        mdi,
      },
    },
    theme: {
      defaultTheme: 'light',
      variations: {
        colors: ['navyblue', 'success', 'info', 'warning', 'highlight'],
        lighten: 3,
        darken: 3,
      },
      themes: {
        light,
        dark,
        pinkLight,
        pinkDark,
      },
    },
  }

  if (params.useAppDefaults) {
    opts.defaults = {
      VAutocomplete: {
        variant: 'underlined',
        color: 'primary',
        density: 'compact',
        itemTitle: 'text',
        itemValue: 'value',
      },
      VBtn: {
        variant: 'elevated',
        class: 'uppercase',
      },
      VCard: {
        variant: 'flat',
      },
      VCheckbox: {
        color: 'primary',
        density: 'compact',
      },
      VCombobox: {
        variant: 'underlined',
        color: 'primary',
        density: 'compact',
      },
      VDataTable: {
        hover: true,
      },
      VDataTableServer: {
        hover: true,
      },
      VMenu: {
        transition: 'fade-transition',
      },
      VProgressLinear: {
        color: 'primary',
      },
      VRadio: {
        color: 'primary',
        density: 'compact',
      },
      VSelect: {
        variant: 'underlined',
        density: 'compact',
      },
      VSwitch: {
        color: 'primary',
      },
      VTabs: {
        color: 'primary',
      },
      VTextarea: {
        variant: 'solo',
        density: 'compact',
      },
      VTextField: {
        variant: 'underlined',
        color: 'primary',
        density: 'compact',
        autocomplete: 'off',
      },
      VToolbar: {
        color: 'surface',
      },
    }
  }

  if (params.extraComponents) {
    opts.components = params.extraComponents
  }

  return opts
}
